import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Paperclip, Plus, RotateCcw, Upload, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { usePermissions } from "@/lib/permissions";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const ALL = "__all__";
const NONE = "__none__";
const EMPTY = { title: "", description: "", location: "", priority: "MEDIUM", owner_user_id: "", owner_name: "", due_date: "", wbs_item_id: "" };

function Tile({ label, value, tone, testId }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 text-2xl font-bold tabular-nums ${tone || "text-foreground"}`}>{value}</p>
    </div>
  );
}

/** Defects found on site: raised, fixed by the owner, then checked and closed by someone else. */
export default function Snags() {
  const { projectId } = useProjectCtx();
  const { can } = usePermissions();
  const canCreate = can("SNAGS", "create");
  const canEdit = can("SNAGS", "edit");
  const canApprove = can("SNAGS", "approve");
  const base = `/projects/${projectId}/snags`;
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [people, setPeople] = useState([]);
  const [activities, setActivities] = useState([]);
  const [filters, setFilters] = useState({ status: ALL, overdue: false, mine: false });
  const [editing, setEditing] = useState(null);          // null | "new" | snag
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: sum }, { data: wbs }, { data: members }] = await Promise.all([
        api.get(base), api.get(`${base}/summary`),
        api.get(`/projects/${projectId}/wbs`, { params: { include_groups: false } }).catch(() => ({ data: [] })),
        api.get(`/projects/${projectId}/members`).catch(() => ({ data: [] })),
      ]);
      setRows(list); setSummary(sum); setActivities((wbs || []).filter((w) => !w.is_group)); setPeople(members || []);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setLoading(false); }
  }, [base, projectId]);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => rows.filter((r) => (filters.status === ALL || r.status === filters.status) && (!filters.overdue || r.overdue)), [rows, filters]);
  const open = rows.find((r) => r.id === openId) || null;

  return (
    <div className="space-y-4" data-testid="snags-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Defects found on site. The owner fixes it, then someone else checks the fix and closes it.</p>
        {canCreate && <Button onClick={() => setEditing("new")} data-testid="snag-new"><Plus className="h-4 w-4" /> Raise a snag</Button>}
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="snag-summary">
          <Tile label="Open" value={summary.open} testId="snag-sum-open" />
          <Tile label="Fixed, to check" value={summary.fixed} tone={summary.fixed ? "text-teal-700" : ""} testId="snag-sum-fixed" />
          <Tile label="Closed" value={summary.closed} testId="snag-sum-closed" />
          <Tile label="Overdue" value={summary.overdue} tone={summary.overdue ? "text-rose-600" : ""} testId="snag-sum-overdue" />
          <Tile label="High priority, not closed" value={summary.high} tone={summary.high ? "text-amber-600" : ""} testId="snag-sum-high" />
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1"><Label className="text-xs">Status</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
            <SelectTrigger className="w-44" data-testid="snag-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>Any status</SelectItem><SelectItem value="OPEN">Open</SelectItem><SelectItem value="FIXED">Fixed, to check</SelectItem><SelectItem value="CLOSED">Closed</SelectItem></SelectContent>
          </Select></div>
        <label className="flex items-center gap-2 pb-2 text-sm"><input type="checkbox" checked={filters.overdue} onChange={(e) => setFilters((f) => ({ ...f, overdue: e.target.checked }))} data-testid="snag-filter-overdue" /> Overdue only</label>
      </div>

      {!loading && shown.length === 0 ? (
        <EmptyState title={rows.length ? "No snags match" : "No snags yet"} description={rows.length ? "Change the filter to see the others." : "Raise one when you find a defect on site."} testId="snags-empty" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <Table data-testid="snag-table">
            <TableHeader><TableRow><TableHead>No.</TableHead><TableHead>Snag</TableHead><TableHead>Priority</TableHead><TableHead>Owner</TableHead><TableHead>Due</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {shown.map((r) => (
                <TableRow key={r.id} className="cursor-pointer hover:bg-muted/40" onClick={() => setOpenId(r.id)} data-testid={`snag-row-${r.number}`}>
                  <TableCell className="font-mono text-xs">{r.number}</TableCell>
                  <TableCell className="max-w-[22rem]"><div className="truncate font-medium">{r.title}</div>{r.location && <div className="truncate text-xs text-muted-foreground">{r.location}</div>}</TableCell>
                  <TableCell><StatusPill value={r.priority} /></TableCell>
                  <TableCell>{r.owner || "—"}</TableCell>
                  <TableCell className={r.overdue ? "font-medium text-rose-600" : "text-muted-foreground"} data-testid={`snag-due-${r.number}`}>{r.due_date ? fmtDate(r.due_date) : "—"}{r.overdue && " (overdue)"}</TableCell>
                  <TableCell><StatusPill value={r.status} />{r.reopen_count > 0 && <span className="ml-1 text-[10px] text-muted-foreground">reopened {r.reopen_count}×</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {editing && <SnagForm base={base} people={people} activities={activities} snag={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={(s) => { setEditing(null); load(); setOpenId(s.id); }} />}
      {open && <SnagDetail base={base} snag={open} can={{ edit: canEdit, approve: canApprove }} onClose={() => setOpenId(null)} onEdit={() => { setEditing(open); setOpenId(null); }} onChanged={load} />}
    </div>
  );
}

function SnagForm({ base, people, activities, snag, onClose, onSaved }) {
  const [f, setF] = useState(snag ? { title: snag.title, description: snag.description || "", location: snag.location || "", priority: snag.priority, owner_user_id: snag.owner_user_id || "", owner_name: snag.owner_name || "", due_date: snag.due_date || "", wbs_item_id: snag.wbs_item_id || "" } : EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const noOwner = !f.owner_user_id && !f.owner_name.trim();
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, description: f.description || null, location: f.location || null, owner_user_id: f.owner_user_id || null, owner_name: f.owner_name.trim() || null, due_date: f.due_date || null, wbs_item_id: f.wbs_item_id || null };
      const { data } = snag ? await api.patch(`${base}/${snag.id}`, body) : await api.post(base, body);
      toast.success(snag ? `${data.number} updated` : `${data.number} raised`);
      onSaved(data);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="snag-form">
        <DialogHeader><DialogTitle>{snag ? `Edit ${snag.number}` : "Raise a snag"}</DialogTitle><DialogDescription>What is wrong, where, and who has to fix it.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>What is wrong</Label><Input value={f.title} onChange={set("title")} maxLength={200} className="mt-1.5" data-testid="snag-title" /></div>
          <div><Label>Details (optional)</Label><Textarea rows={2} value={f.description} onChange={set("description")} className="mt-1.5" data-testid="snag-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Where</Label><Input value={f.location} onChange={set("location")} placeholder="Block A, lobby" className="mt-1.5" data-testid="snag-location" /></div>
            <div><Label>Priority</Label>
              <Select value={f.priority} onValueChange={(v) => setF((x) => ({ ...x, priority: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="snag-priority"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="LOW">Low</SelectItem><SelectItem value="MEDIUM">Medium</SelectItem><SelectItem value="HIGH">High</SelectItem></SelectContent>
              </Select></div>
            <div><Label>Owner (a person on this project)</Label>
              <Select value={f.owner_user_id || NONE} onValueChange={(v) => setF((x) => ({ ...x, owner_user_id: v === NONE ? "" : v }))}>
                <SelectTrigger className="mt-1.5" data-testid="snag-owner"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>Not a person in the system</SelectItem>{people.map((p) => <SelectItem key={p.user_id} value={p.user_id}>{p.user?.full_name || p.user?.email || "Member"}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>…or a contractor / vendor</Label><Input value={f.owner_name} onChange={set("owner_name")} className="mt-1.5" data-testid="snag-owner-name" /></div>
            <div><Label>Fix by</Label><Input type="date" value={f.due_date} onChange={set("due_date")} className="mt-1.5" data-testid="snag-due" /></div>
            <div><Label>Activity (optional)</Label>
              <Select value={f.wbs_item_id || NONE} onValueChange={(v) => setF((x) => ({ ...x, wbs_item_id: v === NONE ? "" : v }))}>
                <SelectTrigger className="mt-1.5" data-testid="snag-activity"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64"><SelectItem value={NONE}>None</SelectItem>{activities.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={saving || !f.title.trim() || noOwner} onClick={save} data-testid="snag-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} {snag ? "Save" : "Raise snag"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SnagDetail({ base, snag: s, can, onClose, onEdit, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [asking, setAsking] = useState(null);          // null | "fix" | "reopen"
  const [text, setText] = useState("");
  const act = async (name, path, body, ok) => {
    setBusy(name);
    try { await api.post(`${base}/${s.id}/${path}`, body); toast.success(ok); setAsking(null); setText(""); onChanged(); }
    catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(null); }
  };
  const upload = async (ev, kind) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setBusy("photo");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`${base}/${s.id}/photos?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added"); onChanged();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(null); ev.target.value = ""; }
  };
  const openPhoto = async (p) => {
    try { const res = await api.get(`${base}/${s.id}/photos/${p.id}/file`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };
  const field = (label, value) => <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p><p className="text-sm">{value || "—"}</p></div>;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="snag-detail">
        <DialogHeader><DialogTitle className="flex flex-wrap items-center gap-2"><Wrench className="h-5 w-5 text-[#910029]" /> {s.number} <StatusPill value={s.status} /><StatusPill value={s.priority} /></DialogTitle>
          <DialogDescription>{s.title}</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {field("Where", s.location)}{field("Activity", s.activity)}{field("Owner", s.owner)}{field("Fix by", s.due_date ? `${fmtDate(s.due_date)}${s.overdue ? " (overdue)" : ""}` : "")}
          {field("Raised by", s.raised_by)}{field("Raised", s.raised_at ? fmtDate(s.raised_at) : "")}
        </div>
        {s.description && <p className="rounded-md bg-muted/40 p-2 text-sm">{s.description}</p>}
        {s.fix_note && <div className="rounded-md border border-teal-200 bg-teal-50 p-2 text-sm" data-testid="snag-fix-note"><b>Fixed{s.fixed_by ? ` by ${s.fixed_by}` : ""}:</b> {s.fix_note}</div>}
        {s.status === "CLOSED" && <div className="rounded-md border border-border p-2 text-sm" data-testid="snag-closed-note"><b>Closed{s.closed_by ? ` by ${s.closed_by}` : ""}</b>{s.closed_at ? ` on ${fmtDate(s.closed_at)}` : ""}{s.close_note ? `: ${s.close_note}` : ""}</div>}

        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Photos</p>
          {s.photos.length === 0 ? <p className="text-sm text-muted-foreground">None yet.</p> : (
            <ul className="space-y-1 text-sm">{s.photos.map((p) => (
              <li key={p.id}><button type="button" className="inline-flex items-center gap-1.5 text-[#910029] hover:underline" onClick={() => openPhoto(p)}><Paperclip className="h-3.5 w-3.5" />{p.filename}</button> <span className="text-xs text-muted-foreground">{p.kind === "FIXED" ? "the repair" : "the defect"}</span></li>))}</ul>)}
          {can.edit && s.status !== "CLOSED" && (
            <div className="mt-2 flex flex-wrap gap-2">
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs hover:bg-muted"><Upload className="h-3.5 w-3.5" /> Add photo of the defect<input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => upload(e, "RAISED")} data-testid="snag-photo-raised" /></label>
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs hover:bg-muted"><Upload className="h-3.5 w-3.5" /> Add photo of the repair<input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => upload(e, "FIXED")} data-testid="snag-photo-fixed" /></label>
            </div>)}
        </div>

        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {s.status === "OPEN" && can.edit && <Button variant="outline" onClick={onEdit} data-testid="snag-edit">Edit</Button>}
            {(s.status === "FIXED" || s.status === "CLOSED") && can.approve && <Button variant="ghost" disabled={!!busy} onClick={() => setAsking("reopen")} data-testid="snag-reopen"><RotateCcw className="h-4 w-4" /> Reopen</Button>}
          </div>
          <div className="flex flex-wrap gap-2">
            {s.status === "OPEN" && can.edit && <Button disabled={!!busy} onClick={() => setAsking("fix")} data-testid="snag-fix"><Wrench className="h-4 w-4" /> Mark fixed</Button>}
            {s.status === "FIXED" && can.approve && <Button disabled={!!busy} onClick={() => act("close", "close", { note: null }, `${s.number} checked and closed`)} data-testid="snag-close">{busy === "close" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Fix checked: close</Button>}
          </div>
        </DialogFooter>

        {asking && (
          <Dialog open onOpenChange={(o) => !o && setAsking(null)}>
            <DialogContent className="max-w-md" data-testid="snag-ask">
              <DialogHeader><DialogTitle>{asking === "fix" ? `What was done to fix ${s.number}?` : `Why is ${s.number} reopened?`}</DialogTitle></DialogHeader>
              <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} data-testid="snag-ask-text" />
              <DialogFooter><Button variant="outline" onClick={() => setAsking(null)}>Cancel</Button>
                <Button disabled={!!busy || text.trim().length < (asking === "fix" ? 3 : 5)} data-testid="snag-ask-submit"
                  onClick={() => (asking === "fix" ? act("fix", "fix", { note: text.trim() }, `${s.number} marked fixed. It is waiting to be checked.`) : act("reopen", "reopen", { reason: text.trim() }, `${s.number} reopened`))}>
                  {asking === "fix" ? "Mark fixed" : "Reopen"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}
